import { useState, type CSSProperties, type PointerEvent, type ReactNode } from 'react';

import { useT, type MessageKey } from '@/i18n';
import { BridgeAppIcon, ClockIcon, LockIcon, PlugIcon, ShieldCheckIcon, TabNearbyIcon, TabQrIcon } from '@/components/Icons';

import styles from './CasesHero.module.css';

/** A CSS module class, never undefined. */
const c = (name: string | undefined): string => name ?? '';

/** Chat has no icon of its own: a bubble drawn in the same line weight as the others. */
function ChatGlyph(): React.JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z" />
    </svg>
  );
}

/** The scenes clockwise from the top, ordered so wide and narrow chips alternate and both sides weigh the same; each chip jumps to its tile. */
const SCENES: readonly { readonly id: string; readonly label: MessageKey; readonly icon: ReactNode }[] = [
  { id: 'passwords', label: 'cases.namePasswords', icon: <LockIcon size={18} /> },
  { id: 'key', label: 'cases.nameKey', icon: <ShieldCheckIcon size={18} /> },
  { id: 'receive', label: 'cases.nameReceive', icon: <TabQrIcon size={18} /> },
  { id: 'time', label: 'cases.nameTime', icon: <ClockIcon size={18} /> },
  { id: 'nearby', label: 'cases.nameNearby', icon: <TabNearbyIcon size={18} /> },
  { id: 'chat', label: 'cases.nameChat', icon: <ChatGlyph /> },
  { id: 'developers', label: 'nav.developers', icon: <PlugIcon size={18} /> },
];

/** The stage is 520 by 440; the chips sit on an ellipse around its centre. */
const W = 520;
const H = 440;
const RX = 190;
const RY = 168;

const POSITIONS = SCENES.map((_, index) => {
  const angle = (-90 + (360 / SCENES.length) * index) * (Math.PI / 180);
  const dx = RX * Math.cos(angle);
  const dy = RY * Math.sin(angle);
  const length = Math.hypot(dx, dy);
  return { x: W / 2 + dx, y: H / 2 + dy, angle, unitX: dx / length, unitY: dy / length };
});

/** The bright arc lights on the inner ring (radius 125), centred on the direction of its chip. */
const ARC_RADIUS = 125;
const ARC_SPAN = 0.62;
const ARC_POINTS = POSITIONS.map((point) => {
  const at = (turn: number): { x: number; y: number } => ({ x: W / 2 + ARC_RADIUS * Math.cos(point.angle + turn), y: H / 2 + ARC_RADIUS * Math.sin(point.angle + turn) });
  const from = at(-ARC_SPAN);
  const to = at(ARC_SPAN);
  return { from, to, path: `M${from.x.toFixed(1)},${from.y.toFixed(1)} A${String(ARC_RADIUS)},${String(ARC_RADIUS)} 0 0 1 ${to.x.toFixed(1)},${to.y.toFixed(1)}` };
});

/** The chips scroll to their tile rather than link to `#id`: the preview runs on a hash router, where `#id` is a page. */
function scrollToTile(id: string): void {
  const tile = document.getElementById(id);
  if (!tile) return;
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  tile.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'start' });
}

/**
 * One Bridge in the middle and every scenario around it. At rest the chips drift a few pixels; the pointer moves the
 * layers a little at different depths; a chip under the pointer lifts and leans toward the logo, a bright arc lights
 * on the inner ring between them, the logo glows on that side and the others step back. No line is drawn.
 * All of it stops under prefers-reduced-motion.
 */
export function CasesHero(): React.JSX.Element {
  const t = useT();
  const [active, setActive] = useState<number | null>(null);

  const onMove = (event: PointerEvent<HTMLDivElement>): void => {
    const box = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - box.left) / box.width - 0.5;
    const y = (event.clientY - box.top) / box.height - 0.5;
    event.currentTarget.style.setProperty('--px', x.toFixed(3));
    event.currentTarget.style.setProperty('--py', y.toFixed(3));
  };
  const onLeave = (event: PointerEvent<HTMLDivElement>): void => {
    event.currentTarget.style.setProperty('--px', '0');
    event.currentTarget.style.setProperty('--py', '0');
    setActive(null);
  };

  return (
    <div className={c(styles.art)}>
      <div
        className={[styles.hub, active === null ? '' : styles.engaged].join(' ')}
        data-testid="cases-hub"
        onPointerMove={onMove}
        onPointerLeave={onLeave}
      >
        <span className={[styles.ring, styles.ring1].join(' ')} aria-hidden="true" />
        <span className={[styles.ring, styles.ring2].join(' ')} aria-hidden="true" />
        <svg className={c(styles.lines)} viewBox={`0 0 ${String(W)} ${String(H)}`} aria-hidden="true">
          <defs>
            {ARC_POINTS.map((arc, index) => (
              <linearGradient key={SCENES[index]?.id} id={`cases-arc-${String(index)}`} gradientUnits="userSpaceOnUse" x1={arc.from.x} y1={arc.from.y} x2={arc.to.x} y2={arc.to.y}>
                <stop offset="0" stopColor="var(--blue)" stopOpacity="0" />
                <stop offset="0.5" stopColor="var(--blue)" stopOpacity="0.95" />
                <stop offset="1" stopColor="var(--blue)" stopOpacity="0" />
              </linearGradient>
            ))}
          </defs>
          {POSITIONS.map((point, index) => (
            <g key={SCENES[index]?.id} className={[styles.beam, active === index ? styles.beamOn : ''].join(' ')}>
              <circle className={c(styles.glow)} cx={W / 2 + point.unitX * 70} cy={H / 2 + point.unitY * 70} r={38} />
              <path className={c(styles.arc)} d={ARC_POINTS[index]?.path ?? ''} stroke={`url(#cases-arc-${String(index)})`} />
            </g>
          ))}
        </svg>
        <span className={c(styles.core)} aria-hidden="true">
          <BridgeAppIcon size={104} />
        </span>
        {SCENES.map((scene, index) => (
          <span
            key={scene.id}
            className={[styles.slot, active === index ? styles.slotOn : ''].join(' ')}
            style={
              {
                left: `${String(POSITIONS[index]?.x ?? 0)}px`,
                top: `${String(POSITIONS[index]?.y ?? 0)}px`,
                '--i': index,
                '--depth': 5 + (index % 3) * 4,
                '--pull-x': `${String(-(POSITIONS[index]?.unitX ?? 0) * 14)}px`,
                '--pull-y': `${String(-(POSITIONS[index]?.unitY ?? 0) * 14)}px`,
              } as CSSProperties
            }
          >
            <span className={c(styles.depth)}>
              <button
                type="button"
                className={[styles.node, active === index ? styles.nodeOn : ''].join(' ')}
                onClick={() => {
                  scrollToTile(scene.id);
                }}
                onPointerEnter={() => {
                  setActive(index);
                }}
                onPointerLeave={() => {
                  setActive(null);
                }}
                onFocus={() => {
                  setActive(index);
                }}
                onBlur={() => {
                  setActive(null);
                }}
              >
                <span className={c(styles.nodeIcon)}>{scene.icon}</span>
                {t(scene.label)}
              </button>
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
